import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, inputCls } from "@/components/AuthShell";

export const ClassDialog = ({ open, onOpenChange, initial, users, onSubmit, saving }) => {
  const [name, setName] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [ids, setIds] = useState([]);
  const [q, setQ] = useState("");
  const teachers = users.filter((u) => u.role === "teacher");
  const students = useMemo(() => users.filter((u) => u.role === "user" && `${u.name} ${u.username || ""} ${u.email}`.toLowerCase().includes(q.toLowerCase())), [users, q]);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || ""); setTeacherId(initial?.teacher_id || ""); setIds(initial?.student_ids || []); setQ("");
  }, [open, initial]);

  const toggle = (id) => setIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FAF7F2] max-w-xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="admin-class-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-right">{initial?.id ? "تعديل الصف" : "إنشاء صف جديد"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit({ name, teacher_id: teacherId, student_ids: ids }); }} className="space-y-4" data-testid="admin-class-form">
          <Field label="اسم الصف"><input required className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="مثال: الصف الرابع - أ" data-testid="admin-class-name" /></Field>
          <Field label="المعلم">
            <select className={inputCls} value={teacherId} onChange={(e) => setTeacherId(e.target.value)} data-testid="admin-class-teacher">
              <option value="">— بدون معلم —</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.username || t.email})</option>)}
            </select>
          </Field>
          {!teachers.length && <p className="text-xs text-amber-800">لا يوجد معلمون بعد. أنشئ حساب معلم من تبويب «المسجلون» واختر الدور «معلم».</p>}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">الطلاب</span>
              <span className="text-xs text-stone-600" data-testid="admin-class-selected-count">تم اختيار {ids.length}</span>
            </div>
            <div className="relative mb-2">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن طالب" className={`${inputCls} pr-10 h-10`} data-testid="admin-class-student-search" />
            </div>
            <div className="max-h-56 overflow-y-auto bg-white border border-[#E5DCCB] divide-y divide-[#E5DCCB]" data-lenis-prevent>
              {students.map((s) => (
                <label key={s.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#FAF7F2]" data-testid={`admin-class-student-${s.id}`}>
                  <Checkbox checked={ids.includes(s.id)} onCheckedChange={() => toggle(s.id)} />
                  <span className="text-sm font-semibold">{s.name}</span>
                  <span className="text-xs text-stone-600 ms-auto" dir="ltr">{s.username || s.email}</span>
                </label>
              ))}
              {!students.length && <p className="p-4 text-sm text-stone-600">لا يوجد طلاب.</p>}
            </div>
          </div>
          <button disabled={saving} className="w-full h-12 rounded-full bg-[#1C1917] text-[#FAF7F2] font-semibold hover:bg-[#8B3A2B] transition-colors disabled:opacity-60" data-testid="admin-class-submit-btn">
            {saving ? "جارٍ الحفظ..." : initial?.id ? "حفظ التعديلات" : "إنشاء الصف"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
